import { useState } from "react";
import { api, useApi, ApiError } from "../lib/api";
import { NeedsRole, useGrant } from "../lib/grant";

/** Listing (GET) and the prefix the cap route lives under (POST …/:id/cap); the role table keys on it. */
export const API_KEYS_PATH = "/v1/api-keys";

interface KeyMeta { id: string; label: string; prefix: string; createdAt: string; disabledAt: string | null; monthlyTokenCap: number | null }
interface Usage { month: string; totalTokens: number; cap: number | null; remaining: number | null }

const inputStyle: React.CSSProperties = {
  padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)",
};

function KeyRow({ k, onChanged }: { k: KeyMeta; onChanged: () => void }) {
  const capPath = `${API_KEYS_PATH}/${k.id}/cap`;
  const usage = useApi<{ usage: Usage }>(`${capPath.replace(/cap$/, "usage")}`, [k.monthlyTokenCap]);
  const grant = useGrant(capPath);
  const [cap, setCap] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(e: React.FormEvent, value: number | null) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api(capPath, { method: "POST", body: JSON.stringify({ monthlyTokenCap: value }) });
      setCap("");
      onChanged();
      usage.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? `${err.message}${err.hint ? ` — ${err.hint}` : ""}` : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr data-testid="key-row" data-key-id={k.id}>
      <td>{k.label}{k.disabledAt ? " (disabled)" : ""}</td>
      <td className="muted">{k.prefix}…</td>
      <td data-testid="key-cap">{k.monthlyTokenCap === null ? "no cap" : k.monthlyTokenCap}</td>
      <td data-testid="key-spent">{usage.data ? usage.data.usage.totalTokens : "…"}</td>
      <td>
        {grant.allowed ? (
          <form onSubmit={(e) => void submit(e, Number(cap))} data-testid="write-form" data-path={capPath} style={{ display: "flex", gap: 8 }}>
            <input aria-label="monthly token cap" type="number" min={1} step={1} required value={cap}
              onChange={(e) => setCap(e.target.value)} style={{ ...inputStyle, width: 140 }} />
            <button disabled={busy} type="submit"
              style={{ padding: "8px 16px", background: "var(--accent)", border: 0, borderRadius: 6, color: "#fff", cursor: "pointer" }}>
              {busy ? "…" : "Set cap"}
            </button>
            <button disabled={busy || k.monthlyTokenCap === null} type="button" onClick={(e) => void submit(e, null)}
              style={{ padding: "8px 12px", background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)", cursor: "pointer" }}>
              Clear
            </button>
          </form>
        ) : <NeedsRole path={capPath} />}
        {problem && <p className="err" style={{ marginTop: 8 }}>{problem}</p>}
      </td>
    </tr>
  );
}

export function ApiKeys() {
  const { data, loading, error, reload } = useApi<{ keys: KeyMeta[] }>(API_KEYS_PATH);
  return (
    <div className="panel">
      <h2>Machine keys</h2>
      <p className="muted">Each key's monthly token cap and what it has spent this calendar month (UTC). Keys are minted through the API.</p>
      {loading && <p className="muted">Loading…</p>}
      {error && <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>}
      {data && data.keys.length === 0 && <p className="muted">No keys yet.</p>}
      {data && data.keys.length > 0 && (
        <table>
          <thead><tr><th>Label</th><th>Prefix</th><th>Monthly cap</th><th>Spent this month</th><th>Cap</th></tr></thead>
          <tbody>{data.keys.map((k) => <KeyRow key={k.id} k={k} onChanged={reload} />)}</tbody>
        </table>
      )}
    </div>
  );
}
